import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import {
  TableWrapper,
  Table,
  TableHeader,
  TableHeaderCell,
  TableRow,
  ColumnResizeHandle,
} from "./TableComponent.styles";
import AscIcon from "@assets/img/chevron_up_yellow.svg";
import DescIcon from "@assets/img/chevron_down_yellow.svg";
import { SortableItem } from "./SortableItem";

interface Column {
  Header: string;
  accessor: string;
  Cell?: (props: { row: any; value: any }) => React.ReactNode;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  enableSort?: boolean;
  centerContent?: boolean;
  hidden?: boolean;
}

interface TableProps {
  onRowClick?: (row: any) => void;
  columns: Column[];
  data: any[];
  onOrderChange?: (updatedData: any[]) => void;
  searchTerm?: string;
  $compactUI?: boolean;
  $isDragDisabled?: boolean;
  activeRow?: any;
  rowIdentifier?: string;
  $resizableColumns?: boolean;
}

const MIN_COLUMN_WIDTH = 40;

// Fit `others` into `budget` (a fraction of the table), split proportionally to
// their starting weights but never below `minUnit`; whatever a floored column
// can't take is redistributed across the columns that still have room.
const fitToBudget = (
  others: string[],
  weights: Record<string, number>,
  budget: number,
  minUnit: number
): Record<string, number> => {
  const result: Record<string, number> = {};
  let remaining = budget;
  let active = [...others];
  let guard = others.length + 1;
  while (active.length > 0 && guard-- > 0) {
    const weightSum = active.reduce((sum, a) => sum + weights[a], 0);
    const share = (a: string): number =>
      weightSum > 0
        ? remaining * (weights[a] / weightSum)
        : remaining / active.length;
    const floored = active.filter((a) => share(a) < minUnit);
    if (floored.length === 0) {
      active.forEach((a) => (result[a] = share(a)));
      break;
    }
    floored.forEach((a) => {
      result[a] = minUnit;
      remaining -= minUnit;
    });
    active = active.filter((a) => !floored.includes(a));
  }
  return result;
};

// Resize model — works in FRACTIONS of the table width so it can never overflow:
// the widths always sum to 1 (100%), and because a `table-layout:fixed` table is
// `width:100%` of its container, percentage columns are pinned to the visible
// panel by construction.
//
// The dragged column absorbs the delta, and the opposite amount is taken from
// (or given to) only the columns the user has NOT resized yet — the "free"
// columns — so previously resized columns keep their widths and earlier edits
// are never undone. If every other column has already been resized, all of them
// act as free so the drag still works. Returns each visible column's width as a
// percentage (0–100).
export const distributeColumnWidths = (
  startWidths: Record<string, number>,
  visibleAccessors: string[],
  affectedAccessor: string,
  rawDelta: number,
  lockedAccessors: string[]
): Record<string, number> => {
  const total = visibleAccessors.reduce((sum, a) => sum + startWidths[a], 0);
  if (total <= 0) return {};

  const minFrac = Math.min(
    MIN_COLUMN_WIDTH / total,
    1 / visibleAccessors.length
  );
  const startFrac: Record<string, number> = {};
  visibleAccessors.forEach((a) => (startFrac[a] = startWidths[a] / total));

  const toPct = (frac: Record<string, number>): Record<string, number> => {
    const pct: Record<string, number> = {};
    Object.keys(frac).forEach((a) => (pct[a] = frac[a] * 100));
    return pct;
  };

  const locked = new Set(lockedAccessors);
  let free = visibleAccessors.filter(
    (a) => a !== affectedAccessor && !locked.has(a)
  );
  // Nothing left untouched — fall back to compensating with every other column.
  if (free.length === 0) {
    free = visibleAccessors.filter((a) => a !== affectedAccessor);
  }
  if (free.length === 0) return toPct({ [affectedAccessor]: 1 });

  const freeBudget = free.reduce((sum, a) => sum + startFrac[a], 0);
  const affectedStart = startFrac[affectedAccessor];
  const affectedMax = affectedStart + (freeBudget - free.length * minFrac);
  const affectedFrac = Math.min(
    Math.max(affectedStart + rawDelta / total, minFrac),
    Math.max(affectedMax, minFrac)
  );
  const newFreeBudget = freeBudget - (affectedFrac - affectedStart);

  const result: Record<string, number> = { [affectedAccessor]: affectedFrac };
  // Locked columns (everything not free and not the dragged one) stay put.
  visibleAccessors.forEach((a) => {
    if (a !== affectedAccessor && !free.includes(a)) result[a] = startFrac[a];
  });
  Object.assign(result, fitToBudget(free, startFrac, newFreeBudget, minFrac));

  return toPct(result);
};

const DraggableRowsTable: React.FC<TableProps> = ({
  $compactUI,
  columns,
  data,
  onRowClick,
  onOrderChange,
  $isDragDisabled,
  activeRow,
  rowIdentifier = "documentId",
  $resizableColumns = false,
}) => {
  const [items, setItems] = useState(data);
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"" | "asc" | "desc">("asc");
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [resizingAccessor, setResizingAccessor] = useState<string | null>(null);
  const lockedColumnsRef = useRef<Set<string>>(new Set());
  const resizingRef = useRef<{
    affectedAccessor: string;
    startX: number;
    startWidths: Record<string, number>;
    visibleAccessors: string[];
    lockedAccessors: string[];
  } | null>(null);

  const handleResizeMove = useCallback((event: PointerEvent): void => {
    const info = resizingRef.current;
    if (!info) return;
    const rawDelta = event.clientX - info.startX;
    const newWidths = distributeColumnWidths(
      info.startWidths,
      info.visibleAccessors,
      info.affectedAccessor,
      rawDelta,
      info.lockedAccessors
    );
    setColumnWidths((prev) => ({ ...prev, ...newWidths }));
  }, []);

  const handleResizeEnd = useCallback((): void => {
    resizingRef.current = null;
    setResizingAccessor(null);
    document.body.style.cursor = "";
    document.removeEventListener("pointermove", handleResizeMove);
    document.removeEventListener("pointerup", handleResizeEnd);
  }, [handleResizeMove]);

  const handleResizeStart = useCallback(
    (
      event: React.PointerEvent<HTMLDivElement>,
      affectedColumn: Column
    ): void => {
      event.preventDefault();
      event.stopPropagation();
      const th = (event.currentTarget as HTMLElement).closest("th");
      const cells = th?.parentElement
        ? Array.from(th.parentElement.children)
        : [];
      const visibleColumns = columns.filter((c) => !c.hidden);
      const startWidths: Record<string, number> = {};
      visibleColumns.forEach((column, index) => {
        const cell = cells[index] as HTMLElement | undefined;
        // Always measure the actual rendered width so the mouse-delta →
        // fraction conversion is correct on every drag (not just the first).
        const measured = cell?.getBoundingClientRect().width ?? 0;
        startWidths[column.accessor] =
          measured > 0 ? measured : MIN_COLUMN_WIDTH;
      });
      lockedColumnsRef.current.add(affectedColumn.accessor);
      resizingRef.current = {
        affectedAccessor: affectedColumn.accessor,
        startX: event.clientX,
        startWidths,
        visibleAccessors: visibleColumns.map((c) => c.accessor),
        lockedAccessors: Array.from(lockedColumnsRef.current),
      };
      setResizingAccessor(affectedColumn.accessor);
      document.body.style.cursor = "col-resize";
      document.addEventListener("pointermove", handleResizeMove);
      document.addEventListener("pointerup", handleResizeEnd);
    },
    [columns, handleResizeMove, handleResizeEnd]
  );

  const handleResizeReset = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      event.preventDefault();
      event.stopPropagation();
      lockedColumnsRef.current = new Set();
      setColumnWidths({});
    },
    []
  );

  useEffect(() => {
    return () => {
      document.removeEventListener("pointermove", handleResizeMove);
      document.removeEventListener("pointerup", handleResizeEnd);
    };
  }, [handleResizeMove, handleResizeEnd]);

  const handleDragEnd = (event: any): void => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => item.id === active.id);
    const newIndex = items.findIndex((item) => item.id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      const updatedItems = arrayMove(items, oldIndex, newIndex);
      setItems(updatedItems);
      onOrderChange?.(updatedItems);
    }
  };
  const handleSort = (column: Column): void => {
    if (column.enableSort) return;
    const accessor = column.accessor;
    let newSortOrder: "" | "asc" | "desc" = "asc";
    if (sortColumn === accessor) {
      newSortOrder = sortOrder === "asc" ? "desc" : "";
    }
    setSortColumn(newSortOrder ? accessor : "");
    setSortOrder(newSortOrder);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      onActivation: ({ event }) => {
        if ($isDragDisabled) {
          event.preventDefault();
        }
      },
    })
  );

  useEffect(() => {
    setItems(data);
  }, [data]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={items.map((item) => item.id)}
        strategy={rectSortingStrategy}
      >
        <TableWrapper variant="darkBg">
          <Table>
            <TableHeader variant="darkBg">
              <TableRow variant="darkBg">
                {columns.map((column, colIndex) => {
                  if (column.hidden) return null;
                  const customWidth = columnWidths[column.accessor];
                  const rightColumn = columns
                    .slice(colIndex + 1)
                    .find((c) => !c.hidden);
                  const showResizeHandle = $resizableColumns && !!rightColumn;
                  return (
                    <TableHeaderCell
                      $centerValues={column.centerContent}
                      $index={colIndex}
                      key={column.accessor + column.Header}
                      $compactUI={$compactUI}
                      onClick={() => handleSort(column)}
                      style={{
                        // Custom widths are percentages of the table so the
                        // columns always sum to 100% and can never overflow the
                        // panel; min/max are cleared so the percentage wins.
                        width:
                          customWidth != null
                            ? `${customWidth}%`
                            : column.width || "auto",
                        minWidth:
                          customWidth != null ? 0 : column.minWidth || "auto",
                        maxWidth:
                          customWidth != null
                            ? "none"
                            : column.maxWidth || "auto",
                      }}
                    >
                      {column.Header}
                      {column.enableSort && sortColumn === column.accessor ? (
                        sortOrder === "asc" ? (
                          <img src={AscIcon} />
                        ) : (
                          <img src={DescIcon} style={{ color: "#fff" }} />
                        )
                      ) : null}
                      {showResizeHandle && rightColumn && (
                        <ColumnResizeHandle
                          data-testid={`column-resize-handle-${column.accessor}`}
                          className={
                            resizingAccessor === column.accessor
                              ? "resizing"
                              : ""
                          }
                          onClick={(e) => e.stopPropagation()}
                          onDoubleClick={(e) => handleResizeReset(e)}
                          onPointerDown={(e) => handleResizeStart(e, column)}
                        />
                      )}
                    </TableHeaderCell>
                  );
                })}
              </TableRow>
            </TableHeader>
            <tbody>
              {items.map((row, index) => (
                <SortableItem
                  activeRow={
                    activeRow?.[rowIdentifier] === row?.[rowIdentifier]
                  }
                  $isDragDisabled={$isDragDisabled}
                  key={row.id}
                  id={row.id}
                  row={row}
                  index={index}
                  columns={columns}
                  $compactUI={$compactUI}
                  onRowClick={onRowClick}
                />
              ))}
            </tbody>
          </Table>
        </TableWrapper>
      </SortableContext>
    </DndContext>
  );
};

export default DraggableRowsTable;
